'use client';

import type { ReactNode } from 'react';

import { useTranslation } from 'react-i18next';

import { cn } from '@kit/ui/utils';

interface KitLineProps {
  name: string;
  /** The kit's items, each with its chosen size. */
  items: string[];
  /** Right-hand side: status, amount, actions. */
  children?: ReactNode;
  className?: string;
}

/**
 * One sold kit shown as a single add-on line: its name and kit label, the items
 * underneath, and whatever the surface puts on the right. Its item lines are
 * still stored (and invoiced) one per item.
 */
export function KitLine({ name, items, children, className }: KitLineProps) {
  const { t } = useTranslation('addons');

  return (
    <div
      className={cn('flex items-start justify-between gap-2', className)}
      data-test="kit-line"
    >
      <div className="min-w-0">
        <div className="flex items-center gap-1.5">
          <span className="truncate">{name}</span>
          <span className="text-muted-foreground shrink-0 text-xs">
            {t('bundles.badge')}
          </span>
        </div>
        {items.length > 0 ? (
          <p className="text-muted-foreground text-xs break-words">{items.join(', ')}</p>
        ) : null}
      </div>
      {children ? <div className="flex shrink-0 items-center gap-2">{children}</div> : null}
    </div>
  );
}
